import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { RSInput, RSButton, RSCheckbox, RSTextarea } from 'reactsymbols-kit';
import { Row, Col } from '../Grid';
import AttributesInspector from './AttributesInspector';
import EditEventController from '../../merode/controllers/EditEventController';
import Styles from '../../styles/colors';
import type { IEventType } from '../../stores/model/metamodel/event_type';
import type { IEventInspector } from '../../stores/ui/event_inspector';
import type { IModelVersion } from '../../merode/controllers/Controller';
import type Report from '../../merode/checks/Report';
import type { AttributeUpdates } from '../../merode/commands/types';
import {
  asRSChange,
  rsValue,
  type RSInputChange,
  type RSTextareaChange,
  type RSCheckboxChange,
} from './rsForm';

interface EventInspectorProps {
  /** The ModelVersion (to pass to the controller). */
  version: IModelVersion;
  /** The event to inspect. */
  event: IEventType;
  /** The EventInspector ui store. */
  ui: IEventInspector;
  /** Callback to call when a report is generated. */
  onReport?: (report: Report) => void;
  /** Callback to close the inspector. */
  requestClose?: () => void;
}

/**
 * Event inspector.
 *
 * Props:
 *
 * - version: The ModelVersion (to pass to the controller)
 * - event: the event to inspect
 * - ui: the EventInspector store
 * - onReport: callback to call when a report is generated.
 * - requestClose: callback to close the inspector.
 *
 * Reset-on-element-swap is handled by key-remount in Inspector/index.js (key on
 * the event id): mounting fresh resets the local renameMethods flag with zero
 * stale frame, replacing the old getDerivedStateFromProps. The store-side
 * `ui.discardChanges()` that the class ran in gDSFP now runs in a cleanup effect
 * (see below). Selecting a different event while dirty already discards via
 * ProjectVersion.confirmable before this remounts, so the fresh mount reads a
 * clean `ui.changes`.
 */
const EventInspector = observer(function EventInspector({
  version,
  event,
  ui,
  onReport,
  requestClose,
}: EventInspectorProps) {
  const [renameMethods, setRenameMethods] = useState(false);

  // The controller is created once per mount (constructor instance field).
  const controllerRef = useRef<EditEventController | null>(null);
  if (controllerRef.current === null) {
    controllerRef.current = new EditEventController(version);
  }
  const controller = controllerRef.current;

  // Defensive discard of pending store edits when this inspector unmounts (key
  // remount on event swap). Mirrors the ui.discardChanges() the class ran in
  // getDerivedStateFromProps on prop swap.
  const uiRef = useRef(ui);
  uiRef.current = ui;
  useEffect(() => {
    return () => {
      uiRef.current.discardChanges();
    };
  }, []);

  const { changes } = ui;

  /**
   * Utitility helpers to:
   * - get the event properties either from the changes or from the event.
   * - check whether a property changed.
   */
  const name = changes.name !== null ? changes.name : event.name;
  const nameChanged = changes.name !== null;
  const description = changes.description !== null ? changes.description : event.description;
  const descriptionChanged = changes.description !== null;

  /**
   * Get the updates to the attributes in the format that the controller
   * expects.
   */
  // The store's `edits`/`additions` views type each property `string | null`
  // (the change model is nullable), but the form guarantees real values for the
  // dirty entries it returns, so they satisfy the command's `AttributeUpdates`
  // (non-null `name`, etc.). Cast at this store→command boundary.
  const attributeUpdates = (): AttributeUpdates => {
    const { attributesInspector } = ui;
    return {
      removals: attributesInspector.removals,
      edits: attributesInspector.edits,
      additions: attributesInspector.additions,
    } as unknown as AttributeUpdates;
  };

  const discardChanges = () => {
    ui.discardChanges();
    setRenameMethods(false);
  };

  const applyChanges = async () => {
    ui.setProjectBusy(true);
    const report = await controller.editEvent(
      event.id,
      {
        name,
        description,
        attributes: attributeUpdates(),
      },
      renameMethods
    );
    if (report.successful) {
      discardChanges();
    }
    ui.setProjectBusy(false);
    if (onReport) onReport(report);
  };

  const deleteEvent = async () => {
    ui.setProjectBusy(true);
    const report = await controller.deleteEvent(event.id);
    if (requestClose) requestClose();
    ui.setProjectBusy(false);
    if (onReport) onReport(report);
  };

  const renderSpecialisationInfo = () => {
    if (event.specialised) {
      // A specialised event always carries its owner event type and inheritance
      // (the metamodel sets them together); `specialised` doesn't narrow those
      // optionals for TS, so assert them here as the original render assumed.
      return (
        <div className="specialisation-info">
          <dl>
            <dt className="block font-semibold">Specialises event: </dt>
            <dd className="mb-[15px] block ps-0 pl-[15px]">{event.ownerEventType!.name}</dd>
            <dt className="block font-semibold">Via inheritance: </dt>
            <dd className="mb-0 block ps-0 pl-[15px]">{event.inheritance!.utf8DisplayString}</dd>
          </dl>
        </div>
      );
    } else {
      return null;
    }
  };

  const renderRenameMethods = () => {
    if (nameChanged) {
      return (
        <div className="check_rename_methods">
          {/*
            Reproduces a quirk of the original class: it read `this.renameMethods`
            here, but the state lived on `this.state.renameMethods` — so there was
            no such instance field and `checked` was always undefined (the checkbox
            never reflected its own state visually). The onChange still drives the
            real `renameMethods` value used by applyChanges. Kept identical on
            purpose; `checked={undefined}` preserves the always-unchecked render.
          */}
          <RSCheckbox
            label="Also rename all methods"
            checked={undefined}
            onChange={asRSChange<RSCheckboxChange>((value: string, checkedState: boolean) =>
              setRenameMethods(checkedState)
            )}
          />
        </div>
      );
    }
  };

  const renderButton = () => {
    if (ui.dirty) {
      return (
        <div className="editEvent p-[15px]">
          <Row gutter={5}>
            <Col md={6}>
              <RSButton
                type="button"
                background={Styles.defaultGradient}
                color={Styles.darkTextColor}
                value="Discard"
                onClick={discardChanges}
              />
            </Col>
            <Col md={6}>
              <RSButton
                type="submit"
                background={Styles.blueGradient}
                value="Apply changes"
                onClick={applyChanges}
              />
            </Col>
          </Row>
        </div>
      );
    } else {
      return (
        <div className="removeEvent p-[15px]">
          <RSButton
            type="button"
            background={Styles.redGradient}
            value="Delete event"
            onClick={deleteEvent}
          />
        </div>
      );
    }
  };

  const check = controller.checkNameInput(name);
  return (
    <div className="event-inspector flex h-full flex-col">
      <div className="event-content flex-1 overflow-auto px-[15px] py-5">
        <RSInput
          label="Event name"
          value={name}
          onChange={asRSChange<RSInputChange>((value: string) =>
            changes.storeChange('name', event.name, value)
          )}
          className={nameChanged ? 'dirty' : ''}
          level={rsValue(check.ok ? null : 'danger')}
          text={rsValue(check.ok ? null : check.message)}
        />
        {renderRenameMethods()}
        {renderSpecialisationInfo()}
        <RSTextarea
          label="Description"
          value={description}
          onChange={asRSChange<RSTextareaChange>((value: string) =>
            changes.storeChange('description', event.description, value)
          )}
          className={descriptionChanged ? 'dirty' : ''}
        />
        <AttributesInspector
          attributes={event.attributes}
          ui={ui.attributesInspector}
          controller={controller}
        />
      </div>
      {renderButton()}
    </div>
  );
});

export default EventInspector;
